/**
 * PublicNavbar — unified top navigation for ALL public marketing
 * surfaces (EcosystemGateway, Security, ModulesFull, Pricing, Login,
 * Signup, FounderApply).
 *
 * Why?
 *   Before this component, every public page rolled its own header.
 *   Some had a "Modules / Security / Pricing / Login" rail, some only
 *   had "Sign In / Get Started" — creating a confusing funnel where the
 *   visitor couldn't reach Security or Modules from the homepage.
 *
 * Design:
 *   • Adapts to BOTH light and dark themes (uses theme-aware classes,
 *     no hard-coded `bg-[#0A0A0A]` etc.).
 *   • Clean SaaS / GRC aesthetic — Inter font, generous tracking, slim
 *     pill CTA.  Matches diligent.com vibe (the user's reference).
 *   • Mobile-first: collapses to a hamburger drawer on <lg.
 *   • Highlights the active route so visitors always know where they
 *     are inside the funnel.
 *
 * Props:
 *   variant: 'auto' (default) | 'light' | 'dark' — forces a fixed
 *            colour scheme regardless of the current theme.  Useful
 *            on Login (always dark canvas) and EcosystemGateway hero.
 */
import React, { useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import Logo from '@/components/Logo';
import ThemeToggle from '@/components/ThemeToggle';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const NAV_LINKS = [
  { to: '/modules-full', label: 'Modules', testid: 'nav-modules' },
  { to: '/security',     label: 'Security', testid: 'nav-security' },
  { to: '/trust',        label: 'Trust', testid: 'nav-trust' },
  { to: '/pricing',      label: 'Pricing', testid: 'nav-pricing' },
];

export default function PublicNavbar({ variant = 'auto' }) {
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  // Tailwind classes are duplicated between variants to keep things
  // legible; the alternative was a runtime style object per element.
  const forceDark = variant === 'dark';

  const linkBase = 'px-3 py-2 text-[12px] uppercase tracking-[0.22em] font-bold transition-colors';
  const linkInactive = forceDark
    ? 'text-white/70 hover:text-white'
    : 'text-slate-700 hover:text-[var(--ve-violet)] dark:text-white/70 dark:hover:text-white';
  const linkActive = forceDark
    ? 'text-[var(--ve-violet-bright)]'
    : 'text-[var(--ve-violet)] dark:text-[var(--ve-violet-bright)]';

  const headerCls = forceDark
    ? 'sticky top-0 z-50 backdrop-blur-md bg-black/65 border-b border-white/10'
    : 'sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/70 dark:bg-black/65 dark:border-white/10';

  const mobileMenuCls = forceDark
    ? 'lg:hidden border-t border-white/10 bg-black/95 backdrop-blur-md'
    : 'lg:hidden border-t border-slate-200/70 dark:border-white/10 bg-white/95 dark:bg-black/95 backdrop-blur-md';

  return (
    <header className={headerCls} data-testid="public-navbar">
      <div className="ve-container flex h-16 items-center justify-between gap-4">
        {/* Logo (left) */}
        <Link to="/" data-testid="public-nav-logo" className="shrink-0 flex items-center">
          <Logo size="sm" showSub={false} noLink />
        </Link>

        {/* Center nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => {
            const isActive = loc.pathname === l.to
              || (l.to === '/modules-full' && loc.pathname.startsWith('/modules'));
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={`${linkBase} ${isActive ? linkActive : linkInactive}`}
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Right actions */}
        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher dark={forceDark} />
          <ThemeToggle compact />
          <Link
            to="/login"
            data-testid="public-nav-login"
            className={`text-[11px] uppercase tracking-[0.22em] font-bold px-4 py-2.5 rounded border transition-colors ${
              forceDark
                ? 'border-[var(--ve-violet-bright)] text-[var(--ve-violet-bright)] hover:bg-[var(--ve-violet)]/10'
                : 'border-[var(--ve-violet)] text-[var(--ve-violet)] hover:bg-[var(--ve-violet)]/10 dark:border-[var(--ve-violet-bright)] dark:text-[var(--ve-violet-bright)]'
            }`}
          >
            Login
          </Link>
          <Link
            to="/pricing"
            data-testid="public-nav-cta"
            className="ve-btn-primary text-[11px] py-2.5 px-4 inline-flex items-center gap-1.5"
          >
            Get Started <ArrowRight size={13} />
          </Link>
        </div>

        {/* Mobile hamburger + always-visible theme toggle */}
        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle compact />
          <button
            className={`p-2 ${forceDark ? 'text-white' : 'text-slate-700 dark:text-white'}`}
            aria-label="Menu"
            onClick={() => setOpen(!open)}
            data-testid="public-nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className={mobileMenuCls} data-testid="public-nav-mobile-menu">
          <nav className="ve-container flex flex-col py-4 gap-1">
            <div className="flex items-center gap-3 px-3 pb-3 mb-2 border-b border-slate-100 dark:border-white/10">
              <LanguageSwitcher dark={forceDark} />
              <ThemeToggle compact />
              <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${forceDark ? 'text-white/55' : 'text-slate-500 dark:text-white/55'}`}>
                Locale &amp; Theme
              </span>
            </div>
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={() => setOpen(false)}
                className={`px-3 py-2 text-sm uppercase tracking-[0.18em] font-bold ${
                  forceDark ? 'text-white/85' : 'text-slate-700 dark:text-white/85'
                }`}
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t border-slate-100 dark:border-white/10 mt-2 pt-3 flex flex-col gap-2">
              <Link
                to="/login"
                onClick={() => setOpen(false)}
                data-testid="public-nav-login-mobile"
                className={`px-3 py-2 text-sm uppercase tracking-[0.18em] font-bold ${
                  forceDark ? 'text-[var(--ve-violet-bright)]' : 'text-[var(--ve-violet)] dark:text-[var(--ve-violet-bright)]'
                }`}
              >
                Login
              </Link>
              <Link
                to="/pricing"
                onClick={() => setOpen(false)}
                data-testid="public-nav-cta-mobile"
                className="mx-3 ve-btn-primary justify-center text-[11px]"
              >
                Get Started <ArrowRight size={13} />
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
